import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import html2canvas from "html2canvas";
import api from "../lib/api";
import ShareCard from "../components/ShareCard";

// Shared pipeline: render the branded card offscreen -> html2canvas PNG ->
// native share sheet (files) with a download fallback. Used by Feed + Inbox nudge.
export function useRoastShare() {
  const [shareItem, setShareItem] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const shareRef = useRef(null);

  useEffect(() => {
    if (!shareItem) return;
    let done = false;
    (async () => {
      await new Promise((r) => setTimeout(r, 80)); // let fonts/layout settle
      try {
        const canvas = await html2canvas(shareRef.current, { scale: 2, backgroundColor: "#09090b", logging: false, useCORS: true });
        const blob = await new Promise((res) => canvas.toBlob(res, "image/png", 0.95));
        const file = new File([blob], "roast-bounty.png", { type: "image/png" });
        const shareText = shareItem.kind === "milestone"
          ? "Ranked up on RoastLit 🔥 think you can roast better? roastlit.app"
          : shareItem.kind === "progress"
          ? "Climbing the roaster ranks on RoastLit 🔥 catch me if you can — roastlit.app"
          : shareItem.kind === "verdict" || shareItem.kind === "beforeafter"
          ? `I asked 5 strangers for the truth. Verdict: ${shareItem.headline?.display || ""} 🔥 get yours → roastlit.app`
          : shareItem.kind === "certificate"
          ? "Officially certified — I got roasted by real humans and survived 🔥🏆 prove you can take it → roastlit.app"
          : shareItem.kind === "aivs"
          ? (shareItem.winner === "human" ? "I pitted a real human against AI. Humans won 💀 roastlit.app" : "AI actually out-roasted the humans this time 🤖 roastlit.app")
          : shareItem.kind === "panel"
          ? "I got the full panel treatment 🔥 roastlit.app"
          : shareItem.kind === "hottake"
          ? `My hot take: ${shareItem.agree_pct}% agree. roastlit.app`
          : shareItem.kind === "battle"
          ? "Roast-Off 🔥 who got roasted harder? Vote → roastlit.app"
          : shareItem.kind === "survival"
          ? `My RoastLit Survival Score: ${shareItem.score}/100 — ${shareItem.label} 🔥 what's yours? roastlit.app`
          : shareItem.kind === "streak"
          ? `Survived ${shareItem.week_count} roast${shareItem.week_count === 1 ? "" : "s"} this week on a ${shareItem.streak_weeks}-week streak 🔥 think you'd last? roastlit.app`
          : shareItem.kind === "quiz"
          ? `${shareItem.result_title} — my RoastLit Arena result 🔥 roastlit.app`
          : shareItem.kind === "labs_result"
          ? `${shareItem.brand}: "${shareItem.winner}" won with ${shareItem.winner_pct}% 🔥 run your own at roastlit.app/labs`
          : "Get judged by 5 real strangers 🔥 roastlit.app";
        let didExport = false;
        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          try { await navigator.share({ files: [file], text: shareText, title: "RoastLit" }); didExport = true; }
          catch (e) { /* user dismissed the share sheet */ }
        } else {
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url; a.download = "roast-bounty.png";
          document.body.appendChild(a); a.click(); a.remove();
          setTimeout(() => URL.revokeObjectURL(url), 4000);
          toast.success("Card saved — post it to your story! 🔥");
          didExport = true;
        }
        if (didExport && shareItem.feedId) { try { await api.post(`/feed/${shareItem.feedId}/share`); } catch (e) {} }
      } catch (e) {
        toast.error("Couldn't generate the card. Try again.");
      } finally {
        if (!done) { setBusyId(null); setShareItem(null); }
      }
    })();
    return () => { done = true; };
  }, [shareItem]);

  const share = (item, id) => {
    if (busyId) return;
    setBusyId(id ?? item.id ?? "card");
    setShareItem(item);
  };

  return { share, busyId, shareRef, shareItem };
}

export function ShareCardPortal({ shareRef, item }) {
  return (
    <div style={{ position: "fixed", left: -10000, top: 0, pointerEvents: "none" }} aria-hidden="true">
      <div ref={shareRef}>{item && <ShareCard item={item} />}</div>
    </div>
  );
}
